import React, { useState, useEffect } from 'react'
import { Link, useParams } from 'react-router-dom'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Navigation, Thumbs, EffectFade } from "swiper/modules";
import { fetchTourBySlug, fetchTours } from '../../services/api';

function TourDetailsMain() {
    const { slug } = useParams();
    const [thumbsSwiper, setThumbsSwiper] = useState(null);
    const [activeTab, setActiveTab] = useState("day-tab1");
    const [tour, setTour] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [countries, setCountries] = useState([]);
    const [relatedTours, setRelatedTours] = useState([]);

    useEffect(() => {
        if (slug) {
            loadTour(slug);
        }
        loadCountries();
    }, [slug]);

    // Load related tours when tour country is available
    useEffect(() => {
        if (tour?.country?.name) {
            loadRelatedTours(tour.country.name);
        }
    }, [tour]);

    const loadRelatedTours = async (countryName) => {
        try {
            const response = await fetchTours(1, '', countryName);
            // Filter out the current tour and limit to 3 tours
            const tours = response.tours
                .filter(t => t.slug !== slug)
                .slice(0, 3);
            setRelatedTours(tours);
        } catch (err) {
            console.error('Error loading related tours:', err);
        }
    };

    const loadCountries = async () => {
        try {
            // Fetch first page to get total pages
            const firstResponse = await fetchTours(1, '', '');
            const totalPages = firstResponse.pagination.meta.last_page || 1;
            let allTours = [...firstResponse.tours];
            
            // Fetch remaining pages if there are more
            if (totalPages > 1) {
                const remainingPromises = [];
                for (let page = 2; page <= totalPages; page++) {
                    remainingPromises.push(fetchTours(page, '', ''));
                }
                
                const remainingResponses = await Promise.all(remainingPromises);
                remainingResponses.forEach(response => {
                    allTours = [...allTours, ...response.tours];
                });
            }
            
            // Extract unique countries with count
            const countryMap = new Map();
            
            allTours.forEach(t => {
                if (t.country && t.country.name) {
                    const countryName = t.country.name;
                    if (!countryMap.has(countryName)) {
                        countryMap.set(countryName, {
                            name: countryName,
                            count: 1
                        });
                    } else {
                        const existing = countryMap.get(countryName);
                        existing.count += 1;
                    }
                }
            });
            
            // Convert to array and sort by name
            const countriesArray = Array.from(countryMap.values())
                .sort((a, b) => a.name.localeCompare(b.name));
            
            setCountries(countriesArray);
        } catch (err) {
            console.error('Error loading countries:', err);
        }
    };

    const loadTour = async (slug) => {
        try {
            setLoading(true);
            setError(null);
            const response = await fetchTourBySlug(slug);
            setTour(response.tour);
            // Set first tab as active when tour loads
            if (response.tour.itinerary && response.tour.itinerary.length > 0) {
                setActiveTab("day-tab1");
            }
        } catch (err) {
            setError(err.message || 'Failed to load tour. Please try again later.');
            console.error('Error loading tour:', err);
        } finally {
            setLoading(false);
        }
    };

    // Convert rating from "3_star" format to number
    const getRatingNumber = (rating) => {
        if (!rating) return 4.8;
        const match = rating.match(/(\d+)_star/);
        return match ? parseFloat(match[1]) : 4.8;
    };

    // Get images for slider - use banner_image and thumbnail_image, or itinerary images
    const getSliderImages = () => {
        if (!tour) return [];
        const images = [];
        if (tour.banner_image) images.push(tour.banner_image);
        if (tour.thumbnail_image && tour.thumbnail_image !== tour.banner_image) {
            images.push(tour.thumbnail_image);
        }
        // Add itinerary images if available
        if (tour.itinerary && tour.itinerary.length > 0) {
            tour.itinerary.forEach(item => {
                if (item.image && !images.includes(item.image)) {
                    images.push(item.image);
                }
            });
        }
        // If no images, use placeholder
        if (images.length === 0) {
            images.push("/assets/img/tour/tour_inner_1.jpg");
        }
        return images;
    };

    // Generate days array from itinerary
    const getDaysFromItinerary = () => {
        if (!tour || !tour.itinerary || tour.itinerary.length === 0) {
            return [{ id: "day-tab1", label: "Day 01" }];
        }
        return tour.itinerary.map((item, index) => ({
            id: `day-tab${index + 1}`,
            label: `Day ${String(index + 1).padStart(2, '0')}`
        }));
    };
    if (loading) {
        return (
            <section className="space">
                <div className="container">
                    <div className="text-center py-5">
                        <p>Loading tour details...</p>
                    </div>
                </div>
            </section>
        );
    }

    if (error || !tour) {
        return (
            <section className="space">
                <div className="container">
                    <div className="alert alert-danger" role="alert">
                        <strong>Error:</strong> {error || 'Tour not found!'}
                    </div>
                </div>
            </section>
        );
    }

    const sliderImages = getSliderImages();
    const days = getDaysFromItinerary();
    const rating = getRatingNumber(tour.rating);

    return (
        <section className="space">
            <div className="container shape-mockup-wrap">
                <div className="row">
                    <div className="col-xxl-8 col-lg-7">
                        <div className="tour-page-single">
                            <div className="slider-area tour-slider1">
                                {sliderImages.length > 0 && (
                                    <>
                                        <Swiper
                                            modules={[Navigation, Thumbs, EffectFade]}
                                            effect="fade"
                                            loop={sliderImages.length > 1}
                                            spaceBetween={10}
                                            navigation={{
                                                prevEl: ".slider-prev",
                                                nextEl: ".slider-next",
                                            }}
                                            thumbs={{ swiper: thumbsSwiper }}
                                            className="swiper th-slider mb-4"
                                            id="tourSlider4"
                                        >
                                            {sliderImages.map((img, index) => (
                                                <SwiperSlide key={index}>
                                                    <div className="tour-slider-img">
                                                        <img src={img} alt={`Slide ${index + 1}`} />
                                                    </div>
                                                </SwiperSlide>
                                            ))}
                                        </Swiper>
                                        {sliderImages.length > 1 && (
                                            <Swiper
                                                modules={[Navigation, Thumbs]}
                                                loop={true}
                                                spaceBetween={25}
                                                slidesPerView={Math.min(3, sliderImages.length)}
                                                watchSlidesProgress
                                                onSwiper={setThumbsSwiper}
                                                className="swiper th-slider tour-thumb-slider"
                                            >
                                                {sliderImages.map((img, index) => (
                                                    <SwiperSlide key={index}>
                                                        <div className="tour-slider-img">
                                                            <img src={img} alt={`Thumbnail ${index + 1}`} />
                                                        </div>
                                                    </SwiperSlide>
                                                ))}
                                            </Swiper>
                                        )}
                                        <button
                                            data-slider-prev="#tourSlider4"
                                            className="slider-arrow style3 slider-prev"
                                        >
                                            <img src="/assets/img/icon/hero-arrow-left.svg" alt="" />
                                        </button>
                                        <button
                                            data-slider-next="#tourSlider4"
                                            className="slider-arrow style3 slider-next"
                                        >
                                            <img src="/assets/img/icon/hero-arrow-right.svg" alt="" />
                                        </button>
                                    </>
                                )}
                            </div>
                            <div className="page-content">
                                <div className="page-meta mb-45">
                                    {tour.is_featured === 1 && (
                                        <Link className="page-tag mr-5" to="/tour">
                                            Featured
                                        </Link>
                                    )}
                                    <span className="ratting">
                                        <i className="fa-sharp fa-solid fa-star" />
                                        <span>{rating}</span>
                                    </span>
                                </div>
                                {tour.sub_title && (
                                    <p className="tour-subtitle mb-2" style={{ fontSize: '1.1rem', color: '#666' }}>
                                        {tour.sub_title}
                                    </p>
                                )}
                                <h2 className="box-title">
                                    {tour.title}
                                </h2>
                                <h4 className="tour-price">
                                    <span className="currency">${tour.price || '0.00'}</span>/Person
                                </h4>
                                {tour.description && (
                                    <div 
                                        className="box-text mb-30" 
                                        dangerouslySetInnerHTML={{ __html: tour.description }}
                                    />
                                )}
                                {tour.itinerary && tour.itinerary.length > 0 && (
                                    <>
                                        <h2 className="box-title">Highlights</h2>
                                        <div className="checklist mb-50">
                                            <ul>
                                                {tour.itinerary.slice(0, 5).map((item, index) => (
                                                    <li key={index}>{item.title}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    </>
                                )}
                                <h2 className="box-title">Basic Information</h2>
                                <div className="destination-checklist mb-50">
                                    <div className="checklist style2">
                                        <ul>
                                            <li>Destination</li>
                                            <li>Duration</li>
                                            <li>Country</li>
                                            <li>City</li>
                                            {tour.type && <li>Type</li>}
                                        </ul>
                                    </div>
                                    <div className="checklist style2">
                                        <ul>
                                            <li>{tour.country?.name || 'N/A'}</li>
                                            <li>{tour.duration ? `${tour.duration} Days` : 'N/A'}</li>
                                            <li>{tour.country?.name || 'N/A'}</li>
                                            <li>{tour.city || 'N/A'}</li>
                                            {tour.type && <li>{tour.type}</li>}
                                        </ul>
                                    </div>
                                </div>
                                <h2 className="box-title">Included and Excluded</h2>
                                <div className="destination-checklist">
                                    {tour.includes && tour.includes.length > 0 && (
                                        <div className="checklist style2 style4">
                                            <ul>
                                                {tour.includes.map((item, index) => (
                                                    <li key={index}>{item.title}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                    {tour.excludes && tour.excludes.length > 0 && (
                                        <div className="checklist style5">
                                            <ul>
                                                {tour.excludes.map((item, index) => (
                                                    <li key={index}>{item.title}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                </div>
                                {tour.itinerary && tour.itinerary.length > 0 && (
                                    <>
                                        <h3 className="page-title mt-50 mb-0">Tour Plan</h3>
                                        <div>
                                            {/* Tab Navigation */}
                                            <ul className="nav nav-tabs tour-tab mt-10" role="tablist">
                                                {days.map((day) => (
                                                    <li className="nav-item" key={day.id} role="presentation">
                                                        <button
                                                            className={`nav-link ${activeTab === day.id ? "active" : ""}`}
                                                            onClick={() => setActiveTab(day.id)}
                                                            type="button"
                                                            role="tab"
                                                            aria-controls={day.id + "-pane"}
                                                            aria-selected={activeTab === day.id}
                                                        >
                                                            {day.label}
                                                        </button>
                                                    </li>
                                                ))}
                                            </ul>

                                            {/* Tab Content */}
                                            <div className="tab-content">
                                                {days.map((day, dayIndex) => {
                                                    const itineraryItem = tour.itinerary[dayIndex];
                                                    return (
                                                        <div
                                                            key={day.id}
                                                            className={`tab-pane fade ${activeTab === day.id ? "show active" : ""}`}
                                                            id={day.id + "-pane"}
                                                            role="tabpanel"
                                                            aria-labelledby={day.id}
                                                            tabIndex={0}
                                                        >
                                                            <div className="tour-grid-plan">
                                                                {itineraryItem && (
                                                                    <>
                                                                        {itineraryItem.image && (
                                                                            <div className="tour-plan-img mb-20">
                                                                                <img src={itineraryItem.image} alt={itineraryItem.title || 'Tour plan'} />
                                                                            </div>
                                                                        )}
                                                                        <h4 className="tour-plan-title mb-15">{itineraryItem.title}</h4>
                                                                        {itineraryItem.description && (
                                                                            <p className="box-text mb-20">{itineraryItem.description}</p>
                                                                        )}
                                                                    </>
                                                                )}
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="col-xxl-4 col-lg-5">
                        <aside className="sidebar-area">
                            <div className="widget widget_search  ">
                                <form className="search-form">
                                    <input type="text" placeholder="Search" />
                                    <button type="submit">
                                        <i className="far fa-search" />
                                    </button>
                                </form>
                            </div>
                            <div className="widget widget_categories">
                                <h3 className="widget_title">Countries</h3>
                                <ul>
                                    {countries.length === 0 ? (
                                        <li>
                                            <span>Loading countries...</span>
                                        </li>
                                    ) : (
                                        countries.map((country, index) => (
                                            <li key={index}>
                                                <Link to={`/tours?country=${encodeURIComponent(country.name)}`}>
                                                    <img src="/assets/img/theme-img/map.svg" alt="" />
                                                    {country.name}
                                                </Link>
                                                <span>({country.count})</span>
                                            </li>
                                        ))
                                    )}
                                </ul>
                            </div>
                            <div className="widget">
                                <h3 className="widget_title">
                                    {tour?.country?.name ? `More Tours in ${tour.country.name}` : 'Related Tours'}
                                </h3>
                                <div className="recent-post-wrap">
                                    {relatedTours.length === 0 ? (
                                        <p className="text-muted">No other tours available in this country.</p>
                                    ) : (
                                        relatedTours.map((t) => (
                                            <div className="recent-post" key={t.id}>
                                                <div className="media-img">
                                                    <Link to={`/tours/${t.slug}`}>
                                                        <img
                                                            src={t.thumbnail_image || '/assets/img/blog/recent-post-1-1.jpg'}
                                                            alt={t.title}
                                                        />
                                                    </Link>
                                                </div>
                                                <div className="media-body">
                                                    <h4 className="post-title">
                                                        <Link className="text-inherit" to={`/tours/${t.slug}`}>
                                                            {t.title}
                                                        </Link>
                                                    </h4>
                                                    <div className="recent-post-meta">
                                                        <span>
                                                            <i className="fa-light fa-clock" />
                                                            {t.duration ? `${t.duration} Days` : 'N/A'}
                                                        </span>
                                                        {t.price && (
                                                            <span style={{ marginLeft: '10px' }}>
                                                                <i className="fa-light fa-dollar-sign" />
                                                                {t.price}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        ))
                                    )}
                                </div>
                            </div>
                        </aside>
                    </div>
                </div>
                <div className="location-map">
                    <h3 className="page-title mt-45 mb-30">Location</h3>
                    <div className="contact-map style3">
                        <iframe
                            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3644.7310056272386!2d89.2286059153658!3d24.00527418490799!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x39fe9b97badc6151%3A0x30b048c9fb2129bc!2sAngfuztheme!5e0!3m2!1sen!2sbd!4v1651028958211!5m2!1sen!2sbd"
                            allowFullScreen=""
                            loading="lazy"
                        />
                        <div className="contact-icon">
                            <img src="/assets/img/icon/location-dot3.svg" alt="" />
                        </div>
                    </div>
                </div>
                <div
                    className="shape-mockup about-shape movingX d-none d-xxl-block"
                    style={{ bottom: "30.5%", right: "0%" }}
                >
                    <img src="/assets/img/normal/about-slide-img.png" alt="shape" />
                </div>
                <div
                    className="shape-mockup about-rating d-none d-xxl-block"
                    style={{ bottom: "40%", right: "-12%" }}
                >
                    <i className="fa-sharp fa-solid fa-star" />
                    <span>4.9k</span>
                </div>
                <div
                    className="shape-mockup about-emoji d-none d-xxl-block"
                    style={{ bottom: "36%", right: "20%" }}
                >
                    <img src="/assets/img/icon/emoji.png" alt="" />
                </div>
                <div
                    className="shape-mockup shape1 d-none d-xxl-block"
                    style={{ bottom: "49%", right: "-12%" }}
                >
                    <img src="/assets/img/shape/shape_1.png" alt="shape" />
                </div>
                <div
                    className="shape-mockup shape2 d-none d-xl-block"
                    style={{ bottom: "45%", right: "-8%" }}
                >
                    <img src="/assets/img/shape/shape_2.png" alt="shape" />
                </div>
                <div
                    className="shape-mockup shape3 d-none d-xxl-block"
                    style={{ bottom: "47%", right: "-5%" }}
                >
                    <img src="/assets/img/shape/shape_3.png" alt="shape" />
                </div>
            </div>
        </section>

    )
}

export default TourDetailsMain
